import { Model } from '@/models/Model';
import { User } from './User';
import { OrderItem } from './OrderItem';
import { Payment } from './Payment';
import axios, { AxiosError, AxiosResponse } from 'axios';
import { Token } from '@atabix/core';

export class Order extends Model {
  protected $name: string = 'Order';
  protected $endpoint: string = '/orders';

  public id?: string;
  public user?: User;
  public order_items?: OrderItem[];
  public payments?: Payment[];
  public links?: any;
  public discount?: string;
  public discount_type?: string;
  public status?: string;
  public note?: string;
  public box_type?: string;
  public user_name?: string;
  public user_type?: string;
  public invoices?: Invoice[];
  public personal_notes?: PersonalNotes[];
  public attachments?: Attachments[];
  public labels?: Label[];
  public peoplevox_id?: string;
  public intro?: string;
  public outro?: string;
  public stylist_id?: string;
  public stylist_name?: string;
  public evaluation?: any;
  public supplier_id?: string;
  public returned_at?: string;
  public final_return_date?: string;
  public peoplevox_allocated_job_status?: string;
  public peoplevox_picked_job_status?: string;
  public peoplevox_dispatched_job_status?: string;
  public last_synced_at?:string;

  public file?: File;

  protected $fillable: string[] = [
    'id',
    'user',
    'note',
    'discount',
    'discount_type',
  ];

  public downloadPdf(endpoint: string) {
    // return client('get', endpoint.replace('http', 'https'), {});
    let headers = {
      Accept: 'application/json',
      'Content-type': 'application/json',
    };

    if (window.location.hostname !== 'localhost') {
      const application = window.location.host.split('.')[0];
      (headers as any)['X-Application'] = application;
    }

    if (Token.get('access')) {
      (headers as any).Authorization = `Bearer ${Token.get('access')}`;
    }

    const client = axios.create({
      headers,
      responseType: 'blob',
    });

    return (client as any)
    ['get'](endpoint, {})
    .then((response: AxiosResponse) => Promise.resolve(response))
    .catch((error: AxiosError) => Promise.reject(error));
  }

  public syncPvx(order: string) {
    const url = `/orders/${order}/get-peoplevox-returns`;

    return this.request('get', url);
  }

  public order(order: Model | string) {
    this.$endpoint = `/orders/${order}`;

    return this;
  }

  public createInvoice(payload: object) {
    const url = '/orders/create-invoices'

    return this.request('post', url, payload);
  }

  public createLabel(orderId: string) {
    const url = `/orders/${orderId}/create-labels`

    return this.request('post', url);
  }

  public deleteInvoice(orderId: string, payload: object) {
    const url = `/orders/${orderId}/delete-invoice`;

    return this.request('delete', url, payload);
  }

  public finalizeOrder(orderId: string, payload: object) {
    const url = `orders/${orderId}/finalize`;

    return this.request('patch', url, payload);
  }

  public evaluateOrder(orderId: string, payload: object) {
    const url = `orders/${orderId}/evaluation`;

    return this.request('patch', url, payload);
  }

  public getShippingStatus(barcode: string) {
    const url = `orders/get-shipping-status/${barcode}`;

    return this.request('get', url);
  }

  public updateOrder(orderId: string, payload: object) {
    const url = `orders/${orderId}`;

    return this.request('patch', url, payload);
  }

  public updateStatusType(payload: object, type: string) {
    const url = `/orders/update-${type}`;

    return this.request('post', url, payload);
  }

  public replacePersonalNote(orderId: string, noteId: string) {
    const url = `/orders/${orderId}/replace-personal-note/${noteId}`;

    return this.request('post', url, {});
  }

  public deleteOrder(order: Model | string) {
    const url = `/orders/${order}`;

    return this.request('delete', url);
  }

  public getRecommendation(orderId: string) {
    const url = `/orders/${orderId}/get-webshop-recommendation`;

    return this.request('get', url);
  }

  public resolvePrimaryKey(): string {
    return '';
  }

  public import(id: string): this {
    this.$endpoint = `/orders/${id}/import`;
    return this;
  }

  public export(id: string): string {
    return `v1/orders/${id}/export`;
  }
}

export interface Invoice {
  next_invoice_id: string,
  type: string;
  number: string;
  created_at: string,
  updated_at: string,
}

export interface PersonalNotes {
  id: string,
  url: string,
  created_at: string,
  updated_at: string,
}

export interface Attachments {
  url: string,
  name: string,
  created_at: string,
}

export interface Label {
  url: string,
  type: string,
  barcode: string,
  track_and_trace_url: string,
  full_status: string,
  status: string,
  sent_at: string,
  delivered_at: string,
  created_at: string,
  updated_at: string,
}
